console.log("App is running in " + import.meta.env.MODE + " mode.")

// Kendo UI license is now handled via telerik-license.txt file or TELERIK_LICENSE env var
// See: https://www.telerik.com/kendo-vue-ui/components/my-license

import { createApp } from "vue"
import { createPinia } from "pinia"
import App from "@/App.vue"
import router from "@/router"
import { msalPlugin } from "@/plugins/msalPlugin"
import { configureMsal, msalInstance } from "@/modules/msal/msalAuthConfig"
import { configureAxios } from "@/modules/axios/axiosConfig"
import { i18n } from "@/modules/i18n/i18nConfig"
import AcErrorHandler from "./modules/common/AcErrorHandler"
// import "@/assets/css/main.css"
import { AppInsightsPlugin } from "vue3-application-insights"
import { aiOptions } from "./modules/applicationinsights/aiOptions"
import PrimeVue from "primevue/config"
import { definePreset } from "@primeuix/themes"
import Aura from "@primeuix/themes/aura"
import { Chart as ChartJS } from "chart.js"

const app = createApp(App)

// Application Insights must be initialized BEFORE axios/msal configuration
// so that it can patch XHR/fetch and properly inject correlation headers
if (import.meta.env.VITE_USE_APPLICATION_INSIGHTS === "true") {
  app.use(AppInsightsPlugin, aiOptions)
}

configureMsal()
configureAxios()

const AcPrimePreset = definePreset(Aura, {
  components: {
    datepicker: {
      date: {
        selectedBackground: "var(--c-blue-400)",
      },
      panel: {
        borderColor: "transparent",
      },
    },

    select: {
      root: {
        background: "transparent",
        color: "{primary.700}",
        borderColor: "{primary.700}",
        hoverBorderColor: "{primary.500}",
        focusBorderColor: "{primary.500}",
        sm: {
          fontSize: ".75rem",
        },
      },
      dropdown: { color: "{primary.700}" },
    },

    multiselect: {
      root: {
        placeholderColor: "{primary.700}",
        color: "{primary.700}",

        borderColor: "{primary.700}",
        hoverBorderColor: "{primary.500}",
        focusBorderColor: "{primary.500}",
      },
      list: { header: { padding: "var(--p-multiselect-option-padding)" } },
      option: { color: "{primary.700}" },
      dropdown: { color: "{primary.700}" },
    },

    button: {
      colorScheme: {
        light: {
          outlined: {
            primary: {
              color: "{primary.700}",
              borderColor: "{primary.700}",
            },
          },
        },
      },
    },
  },

  semantic: {
    colorScheme: {
      light: {
        surface: {
          0: "#fff",
          50: "var(--portal-offwhite)",
          100: "var(--portal-bg)",
          200: "var(--portal-bg)",
          300: "var(--portal-light-blue)",
          400: "var(--portal-light-blue)",
          500: "var(--portal-light-blue)",
          600: "var(--portal-dark-blue)",
          700: "var(--portal-dark-blue)",
          800: "var(--portal-dark-blue)",
          900: "var(--portal-text)",
        },

        primary: {
          0: "#fff",
          50: "var(--portal-offwhite)",
          100: "var(--portal-bg)",
          200: "var(--portal-bg)",
          300: "var(--portal-light-blue)",
          400: "var(--portal-light-blue)",
          500: "var(--portal-light-blue)",
          600: "var(--portal-dark-blue)",
          700: "var(--portal-dark-blue)",
          800: "var(--portal-dark-blue)",
          900: "var(--portal-text)",
        },

        secondary: {
          0: "#fff",
          50: "var(--portal-grey)",
          100: "var(--portal-grey)",
          200: "var(--portal-grey)",
          300: "var(--portal-stroke)",
          400: "var(--portal-stroke)",
          500: "var(--portal-stroke)",
          600: "var(--portal-stroke)",
          700: "var(--portal-text)",
          800: "var(--portal-text)",
          900: "var(--portal-text)",
          950: "var(--portal-text)",
        },

        highlight: {
          color: "var(--portal-dark-blue)",
        },

        list: {
          padding: "0",
          option: { borderRadius: "0" },
        },

        formField: {
          borderRadius: "var(--s-2)",
          borderWidth: "0",
          color: "var(--portal-dark-blue)",
        },
      },
    },
  },
})

app.use(createPinia())
app.use(router)
app.use(msalPlugin, msalInstance)
app.use(i18n)
app.use(PrimeVue, {
  inputVariant: "filled",
  theme: {
    preset: AcPrimePreset,
    cssLayer: {
      name: "primevue",
      order: "theme, base, primevue, components, utilities",
    },
  },
  pt: {
    tab: { root: "rounded-lg" },
  },
})

ChartJS.defaults.font.family = "Inter, sans-serif"

app.config.errorHandler = (err) => AcErrorHandler.onError(err)

app.mount("#app")
